import { useState } from "react";
import { Button, TextField } from "@legends/ui";
import { forgotPassword } from "./api";

export interface ForgotPasswordScreenProps {
  /** Return to the sign-in screen. */
  onBackToLogin: () => void;
}

// Request a password reset (LSC-614). Styled like SignUpScreen/LoginScreen (the Cave
// sign-in card). The response is ALWAYS generic — we show the same "check your email"
// confirmation whether or not the address is a verified local account, so the screen
// never reveals account existence or type. SSO fans recover through their provider.
export default function ForgotPasswordScreen({ onBackToLogin }: ForgotPasswordScreenProps) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy || email.trim() === "") return;
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err: unknown) {
      // Even a transport error shouldn't reveal state — but surface a rate-limit (429)
      // or network problem so the fan knows to retry, without implying the email exists.
      setError(err instanceof Error ? err.message : "Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: 360,
          animation: "cnPop var(--dur) var(--ease-out)",
        }}
      >
        {sent ? (
          <div style={{ display: "grid", gap: 14 }}>
            <h1 style={{ margin: 0, fontSize: "var(--fs-h2)", letterSpacing: "var(--ls-tight)" }}>Check your email</h1>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-dim)", lineHeight: 1.6 }}>
              If an account with that email exists, we've sent a link to reset your password. The link expires in an
              hour. If you signed up with Google or Apple, use that button to sign in instead.
            </p>
            <Button variant="accent" full onClick={onBackToLogin}>
              Back to sign in
            </Button>
          </div>
        ) : (
          <>
            <h1 style={{ margin: "0 0 4px", fontSize: "var(--fs-h2)", letterSpacing: "var(--ls-tight)" }}>Reset your password</h1>
            <p style={{ margin: "0 0 4px", fontSize: "var(--fs-sm)", color: "var(--text-dim)" }}>
              Enter your email and we'll send a reset link.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              style={{ marginTop: 16, display: "grid", gap: 14 }}
            >
              <TextField label="Email" name="email" type="email" value={email} onChange={setEmail} onSubmit={submit} autoFocus full />
              {error && (
                <p role="alert" style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--alert-2)" }}>
                  {error}
                </p>
              )}
              <Button variant="accent" full disabled={busy || email.trim() === ""} type="submit">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <button
              type="button"
              onClick={onBackToLogin}
              style={{
                marginTop: 16,
                background: "none",
                border: "none",
                color: "var(--accent)",
                cursor: "pointer",
                fontSize: "var(--fs-sm)",
                width: "100%",
              }}
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </main>
  );
}
